import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  PageShell,
  SectionCard,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import {
  useDeleteOpenApiPartnerMutation,
  useOpenApiPartnerHistoriesQuery,
  useOpenApiPartnerQuery,
  useReissueOpenApiKeysMutation,
  useUpdateOpenApiPartnerMutation,
} from '../hooks/useOpenApiPartners';
import { OPEN_API_PARTNER_WRITE_ROLES } from '../lib/openApiPartnerRbac';
import { formatKstDateTime } from '../lib/partnerFormat';
import { PartnerForm } from '../components/PartnerForm';
import { PartnerHistoryTable } from '../components/PartnerHistoryTable';
import { IssuedKeysDialog, type IssuedKeyValues } from '../components/IssuedKeysDialog';
import { DeletePartnerDialog, ReissueKeysDialog } from '../components/PartnerActionDialogs';
import { PartnerStatusBadge } from '../components/PartnerStatusBadge';

const LIST_PATH = '/settings/open-api-partners';

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex gap-3 text-[13px]">
    <span className="w-28 shrink-0" style={{ color: 'var(--content-muted)' }}>
      {label}
    </span>
    <span className="min-w-0 break-all" style={{ color: 'var(--content-default)' }}>
      {children}
    </span>
  </div>
);

/** 제휴사 상세 — 정보(수정)·변경 이력 탭, 키 재발급·삭제. OPS_ADMIN 은 조회 전용. */
export const OpenApiPartnerDetailPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { partnerId } = useParams<{ partnerId: string }>();
  const canWrite = useHasRole(OPEN_API_PARTNER_WRITE_ROLES);
  const query = useOpenApiPartnerQuery(partnerId);
  const histories = useOpenApiPartnerHistoriesQuery(partnerId);
  const update = useUpdateOpenApiPartnerMutation();
  const reissue = useReissueOpenApiKeysMutation();
  const remove = useDeleteOpenApiPartnerMutation();
  const [reissueOpen, setReissueOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [issued, setIssued] = useState<IssuedKeyValues | null>(null);

  const title = t('openApiPartners.detailTitle', '제휴사 상세');
  if (query.isPending) {
    return (
      <PageShell title={title} backHref={LIST_PATH}>
        <Skeleton className="h-48 w-full" />
      </PageShell>
    );
  }
  if (query.isError || !partnerId) {
    return (
      <PageShell title={title} backHref={LIST_PATH}>
        <p role="alert" className="text-[13px]" style={{ color: 'var(--status-danger-fg)' }}>
          {query.error?.message ?? t('openApiPartners.errors.notFound', '제휴사를 찾을 수 없습니다.')}
        </p>
      </PageShell>
    );
  }
  const partner = query.data;

  return (
    <PageShell
      title={partner.partnerName}
      backHref={LIST_PATH}
      backLabel={t('openApiPartners.title', '매출 수집 API 연동 관리')}
      actions={
        canWrite ? (
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setReissueOpen(true)} data-testid="open-api-partner-reissue">
              {t('openApiPartners.reissue.title', '키 재발급')}
            </Button>
            <Button type="button" variant="danger" onClick={() => setDeleteOpen(true)} data-testid="open-api-partner-delete">
              {t('openApiPartners.delete.submit', '삭제')}
            </Button>
          </div>
        ) : null
      }
    >
      <SectionCard>
        <div className="flex flex-col gap-2">
          <InfoRow label={t('openApiPartners.columns.status', '상태')}>
            <PartnerStatusBadge partner={partner} />
          </InfoRow>
          <InfoRow label={t('openApiPartners.columns.partnerKey', '제휴사 키')}>
            <code className="font-mono">{partner.partnerKeyMasked ?? '—'}</code>
          </InfoRow>
          <InfoRow label={t('openApiPartners.columns.shopKey', '쇼핑몰 키')}>
            <code className="font-mono">{partner.shopKeyMasked ?? '—'}</code>
          </InfoRow>
          <InfoRow label={t('openApiPartners.columns.lastCalledAt', '마지막 호출')}>
            {formatKstDateTime(partner.lastCalledAt) ?? '—'}
          </InfoRow>
          <InfoRow label={t('openApiPartners.columns.createdAt', '등록')}>
            {formatKstDateTime(partner.createdAt) ?? '—'} {partner.createdBy ? `· ${partner.createdBy}` : ''}
          </InfoRow>
          <InfoRow label={t('openApiPartners.columns.updatedAt', '수정일')}>
            {formatKstDateTime(partner.updatedAt) ?? '—'} {partner.updatedBy ? `· ${partner.updatedBy}` : ''}
          </InfoRow>
        </div>
      </SectionCard>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">{t('openApiPartners.tabs.info', '설정')}</TabsTrigger>
          <TabsTrigger value="history">{t('openApiPartners.tabs.history', '변경 이력')}</TabsTrigger>
        </TabsList>
        <TabsContent value="info">
          <SectionCard>
            {/* updatedAt 이 바뀌면(저장·재발급 후 재조회) 폼을 새 값으로 다시 연다. */}
            <PartnerForm
              key={partner.updatedAt ?? partner.partnerId}
              initial={partner}
              readOnly={!canWrite}
              submitting={update.isPending}
              errorMessage={update.error?.message ?? null}
              submitLabel={t('openApiPartners.actions.save', '저장')}
              onSubmit={(payload) =>
                update.mutate(
                  { partnerId, payload },
                  { onSuccess: () => toast.success(t('openApiPartners.saved', '저장했습니다. 최대 30초 안에 반영됩니다.')) },
                )
              }
            />
          </SectionCard>
        </TabsContent>
        <TabsContent value="history">
          <SectionCard padded={false}>
            <PartnerHistoryTable
              histories={histories.data}
              loading={histories.isPending}
              errorMessage={histories.error?.message ?? null}
            />
          </SectionCard>
        </TabsContent>
      </Tabs>

      <ReissueKeysDialog
        open={reissueOpen}
        partnerName={partner.partnerName}
        submitting={reissue.isPending}
        errorMessage={reissue.error?.message ?? null}
        onClose={() => {
          setReissueOpen(false);
          reissue.reset();
        }}
        onSubmit={(target, reason) =>
          reissue.mutate(
            { partnerId, target, reason },
            {
              onSuccess: (res) => {
                setIssued({ partnerName: res.partner.partnerName, partnerKey: res.partnerKey, key: res.key });
                reissue.reset();
                setReissueOpen(false);
              },
            },
          )
        }
      />
      <DeletePartnerDialog
        open={deleteOpen}
        partnerName={partner.partnerName}
        submitting={remove.isPending}
        errorMessage={remove.error?.message ?? null}
        onClose={() => {
          setDeleteOpen(false);
          remove.reset();
        }}
        onSubmit={(reason) =>
          remove.mutate(
            { partnerId, reason },
            {
              onSuccess: () => {
                toast.success(t('openApiPartners.deleted', '삭제했습니다.'));
                void navigate(LIST_PATH);
              },
            },
          )
        }
      />
      <IssuedKeysDialog issued={issued} onClose={() => setIssued(null)} />
    </PageShell>
  );
};
